import { useMemo, useState } from "react";
import { Plus, X as XIcon, ShoppingCart } from "lucide-react";
import { Spinner } from "@cntrl/ui";
import { pushToast } from "../../app/toasts.js";
import { useStock, useAddStock, useRetractStock, type StockAsk } from "../../app/hooks/useBuses.js";
import styles from "./BusStock.module.scss";

/**
 * Bus Stock — one coach's restock list (ART-147 §7.6). Collaborative: any Pro band seat adds; RETRACT is
 * author-only (the ✕ shows only on `mine`). Three DISTINCT renders — loading, checked-and-empty
 * ("Nothing needed", a claim made only after the read settles), and failed — never collapsed (the
 * three-outcomes rule). "Neither ever hides an ask": every ask for this coach is shown, whoever added it.
 */
export function BusStockPanel({ vehicleId, compact }: { vehicleId: string; compact?: boolean }) {
  const q = useStock();
  const add = useAddStock();
  const retract = useRetractStock();
  const [item, setItem] = useState("");
  const [qty, setQty] = useState(1);

  const asks: StockAsk[] = (q.data ?? []).filter((a) => a.vehicleId === vehicleId);

  const submit = () => {
    const trimmed = item.trim();
    if (!trimmed) return;
    add.mutate({ vehicleId, item: trimmed, qty }, {
      onSuccess: () => { setItem(""); setQty(1); },
      onError: (e) => pushToast(e.message, "error"),
    });
  };

  return (
    <div className={`${styles.panel} ${compact ? styles.compact : ""}`}>
      <div className={styles.head}><ShoppingCart size={14} /> Bus stock{asks.length > 0 ? ` · ${asks.length}` : ""}</div>

      {q.isLoading ? (
        <div className={styles.state}><Spinner size={16} /></div>
      ) : q.isError ? (
        <p className={styles.state}>Couldn't load the stock list.</p>
      ) : asks.length === 0 ? (
        // No "Nothing needed." line (Ryan, ART-214) — the Add-item box below is enough to say the list is empty.
        null
      ) : (
        <ul className={styles.list}>
          {asks.map((a) => (
            <li key={a.askId} className={styles.row}>
              <span className={styles.qty}>{a.qty}×</span>
              <span className={styles.item}>{a.item}</span>
              <span className={styles.by}>{a.personName}</span>
              {a.mine && (
                <button
                  type="button"
                  className={styles.x}
                  aria-label="Retract"
                  onClick={() => retract.mutate(a.askId, { onError: (e) => pushToast(e.message, "error") })}
                ><XIcon size={12} /></button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        className={styles.add}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <input
          className={styles.input}
          placeholder="Add item"
          value={item}
          maxLength={120}
          onChange={(e) => setItem(e.target.value)}
        />
        <input
          className={styles.qtyInput}
          type="number"
          min={1}
          max={99}
          value={qty}
          aria-label="Quantity"
          onChange={(e) => setQty(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
        />
        <button type="submit" className={styles.addBtn} disabled={!item.trim() || add.isPending} aria-label="Add">
          <Plus size={14} />
        </button>
      </form>
    </div>
  );
}

/**
 * The TM view — the whole fleet's asks rolled into a shopping list: identical items summed across every coach
 * (§7.6). Grouped case-insensitively but LABELLED with the first spelling seen, sorted by total desc. Three
 * renders like the panel. Never hides an ask — every item that was asked for is a line.
 */
export function FleetShoppingList() {
  const q = useStock();
  const rolled = useMemo(() => {
    const by = new Map<string, { label: string; total: number }>();
    for (const a of q.data ?? []) {
      const key = a.item.trim().toLowerCase();
      const hit = by.get(key);
      if (hit) hit.total += a.qty;
      else by.set(key, { label: a.item.trim(), total: a.qty });
    }
    return [...by.values()].sort((x, y) => y.total - x.total || x.label.localeCompare(y.label));
  }, [q.data]);

  return (
    <div className={styles.rollup}>
      <div className={styles.head}><ShoppingCart size={14} /> Shopping list · whole fleet</div>
      {q.isLoading ? (
        <div className={styles.state}><Spinner size={16} /></div>
      ) : q.isError ? (
        <p className={styles.state}>Couldn't load the shopping list.</p>
      ) : rolled.length === 0 ? (
        <p className={styles.empty}>Nothing needed across the fleet.</p>
      ) : (
        <ul className={styles.rollupList}>
          {rolled.map((r) => (
            <li key={r.label} className={styles.rollupRow}>
              <span className={styles.rollupItem}>{r.label}</span>
              <span className={styles.rollupTotal}>×{r.total}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
